import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import validate from "validate.js";
import { change, Field, formValueSelector, isDirty, reduxForm } from "redux-form";
import { connect, useDispatch, useStore } from "react-redux";
import moment from "moment-timezone";
import FormSelect from "../../form/redux-form/FormSelect";
import FormDateField from "../../form/redux-form/FormDateField";
import FormDateTimeField from "../../form/redux-form/FormDateTimeField";
import {
  ReportDefaultTypes,
  ReportTypesForClientDialog,
  ReportTypesForOperatorRooveeDialog,
  ReportTypesForOperatorAdministratorRooveeDialog,
} from "../../../models/reports/ReportTypeForDialog";
import { hasPermission, permissions } from "../../../utils/authorization";
import { ReportDataFor } from "../../../models/reports/ReportDataFor";
import { UserRoles } from "../../../models/auth/AuthModel";
import ReportTypeFields from "../../../models/reports/ReportTypeFields";
import getHostFlags from "../../../utils/getHostFlags";

export const reportsWithSelectableGeneratedFor = [
  ReportTypeFields.dayByDayAccountingReport.value,
  ReportTypeFields.monthlyAccountingReport.value,
  ReportTypeFields.rentalByRentalAccountingReport.value,
  ReportTypeFields.braMonthlyAccountingReport.value,
];

export const periodicBikesUnit = (type) => {
  if (type === ReportTypeFields.monthlyBikesReport.value) return "month";
  if (type === ReportTypeFields.yearlyBikesReport.value) return "year";
  return null;
};

export const periodicBikesRange = (type, date) => {
  const unit = periodicBikesUnit(type);
  if (!unit || !date || !date.isValid()) return { startDate: null, endDate: null };
  const startDate = moment.tz(
    { year: date.year(), month: unit === "year" ? 0 : date.month(), date: 1 },
    "Europe/Warsaw",
  ).startOf("day");
  return { startDate, endDate: startDate.clone().endOf(unit) };
};

const usesFullDayPeriod = (type) => [ReportTypeFields.hourlyRentalsReport.value, ReportTypeFields.hourlyRentalDurationReport.value, ReportTypeFields.stationConnectionsReport.value, ReportTypeFields.bikeDefectsReport.value, ReportTypeFields.clientFundsReport.value, ReportTypeFields.rentalDurationByTypeReport.value].includes(type);

export const hourlyRentalsDate = (date, end = false) => {
  if (!date || !date.isValid()) return date;
  const local = moment.tz({ year: date.year(), month: date.month(), date: date.date() }, "Europe/Warsaw");
  return end ? local.endOf("day") : local.startOf("day");
};

export const formValidation = (values) => {
  const constraints = {
    type: {
      presence: { message: "Pole jest wymagane" },
    },
  };

  const errors = validate(values, constraints, { fullMessages: false }) || {};
  if (!values.startDate) errors.startDate = "Data jest wymagana";
  if (!values.endDate) errors.endDate = "Data jest wymagana";
  if (values.sourceReportType !== 1 && values.type === ReportTypeFields.stationPopularityReport.value &&
      values.startDate && values.endDate && values.endDate.isAfter(values.startDate.clone().utc().add(1, "year"))) {
    errors.endDate = "Wybierz okres nie dłuższy niż rok";
  }
  if (values.sourceReportType !== 1 && usesFullDayPeriod(values.type)) {
    const { startDate, endDate } = values;
    if (!startDate || !startDate.isValid()) errors.startDate = "Wybierz poprawny dzień";
    if (!endDate || !endDate.isValid()) errors.endDate = "Wybierz poprawny dzień";
    if (startDate && startDate.isValid() && endDate && endDate.isValid()) {
      const start = moment(startDate).tz("Europe/Warsaw");
      const end = moment(endDate).tz("Europe/Warsaw");
      if (!start.isSame(start.clone().startOf("day")) ||
          !end.isSame(end.clone().endOf("day"), "second")) {
        errors.startDate = "Wybierz pełne dni według czasu polskiego";
      }
      if (!end.isBefore(moment.tz("Europe/Warsaw").startOf("day"))) {
        errors.endDate = "Wybierz zakończony dzień (najpóźniej wczoraj)";
      }
      if (values.type !== ReportTypeFields.clientFundsReport.value &&
          end.clone().startOf("day").add(1, "day").isAfter(start.clone().add(1, "year"))) {
        errors.endDate = "Wybierz okres nie dłuższy niż rok";
      }
    }
  }
  if (values.sourceReportType !== 1 && periodicBikesUnit(values.type) && values.startDate && values.endDate) {
    const expected = periodicBikesRange(values.type, values.startDate);
    if (!expected.startDate || !values.startDate.isSame(expected.startDate) ||
        !values.endDate.isSame(expected.endDate)) {
      errors.startDate = "Wybierz pełny miesiąc lub rok kalendarzowy";
    }
  }

  if (
    values.startDate &&
    values.endDate &&
    values.endDate.isBefore(values.startDate)
  ) {
    errors.endDate = "Data musi być późniejsza";
  }

  if (
    values.type === ReportTypeFields.dailyReport.value &&
    values.startDate &&
    values.endDate &&
    !values.startDate.isSame(values.endDate, "day")
  ) {
    errors.startDate = "Raport dzienny może obejmować tylko jeden dzień";
  }

  if (
    values.additionalStartDate &&
    values.additionalEndDate &&
    values.additionalEndDate.isBefore(values.additionalStartDate)
  ) {
    errors.additionalEndDate = "Data musi być późniejsza";
  }

  return errors;
};

export const getReportsForDialogByUserRole = (userRole, dailyOnly = false) => {
  if (dailyOnly) return { dailyReport: ReportTypeFields.dailyReport };
  switch (userRole) {
    case UserRoles.client.value:
      return ReportTypesForClientDialog;
    case UserRoles.operatorRoovee.value:
    case UserRoles.operatorAdministrator.value:
      return { ...ReportDefaultTypes, ...ReportTypesForOperatorRooveeDialog };
    case UserRoles.operatorAdministratorRoovee.value:
      return {
        ...ReportDefaultTypes,
        ...ReportTypesForOperatorAdministratorRooveeDialog,
      };
    default:
      return ReportDefaultTypes;
  }
};

let GenerateReportForm = ({
  handleSubmit,
  customReportList,
  sourceReportType,
  reportType,
  canGenerateReportWithChosenData,
  dailyOnly,
  userRole,
}) => {
  const { isDevOrRoovee } = getHostFlags();
  const dispatch = useDispatch();
  const store = useStore();
  const isDailyReport = reportType === ReportTypeFields.dailyReport.value;
  const periodicUnit = sourceReportType === 0 ? periodicBikesUnit(reportType) : null;
  const hasFullDayPeriod = sourceReportType === 0 && usesFullDayPeriod(reportType);

  useEffect(() => {
    if (!hasFullDayPeriod) return;
    // Read on entering this calendar so later date edits (including clearing a field) stay intact.
    const state = store.getState();
    if (isDirty("generateReport")(state, "startDate", "endDate")) {
      const { startDate, endDate } = selector(state, "startDate", "endDate");
      dispatch(change("generateReport", "startDate", hourlyRentalsDate(startDate)));
      dispatch(change("generateReport", "endDate", hourlyRentalsDate(endDate, true)));
      return;
    }
    const previousMonth = moment.tz("Europe/Warsaw").subtract(1, "month");
    dispatch(change("generateReport", "startDate", previousMonth.clone().startOf("month")));
    dispatch(change("generateReport", "endDate", previousMonth.clone().endOf("month")));
  }, [dispatch, hasFullDayPeriod, store]);

  useEffect(() => {
    if (!periodicUnit) return;
    const range = periodicBikesRange(reportType, moment.tz("Europe/Warsaw").subtract(1, periodicUnit));
    dispatch(change("generateReport", "startDate", range.startDate));
    dispatch(change("generateReport", "endDate", range.endDate));
  }, [dispatch, periodicUnit, reportType]);

  const handlePeriodicDateChange = (...args) => {
    const range = periodicBikesRange(reportType, args[1]);
    dispatch(change("generateReport", "endDate", range.endDate));
  };

  useEffect(() => {
    if (!isDailyReport) return;

    const reportDate = moment().local().subtract(1, "day");
    dispatch(
      change("generateReport", "startDate", reportDate.clone().startOf("day")),
    );
    dispatch(
      change("generateReport", "endDate", reportDate.clone().endOf("day")),
    );
  }, [dispatch, isDailyReport]);

  const handleDailyReportDateChange = (...args) => {
    const value = args[1];
    dispatch(
      change(
        "generateReport",
        "endDate",
        value ? value.clone().endOf("day") : null,
      ),
    );
  };

  const sourceTypeList = [
    { name: "Standardowy", value: 0 },
    ...(!dailyOnly ? [{ name: "Własny", value: 1 }] : []),
  ];

  const accountingReportIds = [...reportsWithSelectableGeneratedFor];

  if (isDevOrRoovee) {
    accountingReportIds.push(
      ReportTypeFields.specialMonthlyAccountingReport.value,
    );
  }

  const canShowAdditionalRange = useMemo(() => {
    if (!reportType) return false;

    return [ReportTypeFields.globalDebtCollectionReport.value].includes(
      reportType,
    );
  }, [reportType]);

  return (
    <form onSubmit={handleSubmit}>
      <div>
        <Field
          name="sourceReportType"
          component={FormSelect}
          label="Typ raportu"
          values={sourceTypeList}
          fullWidth
        />
      </div>
      <div>
        {sourceReportType === 0 && (
          <Field
            name="type"
            component={FormSelect}
            label="Raport"
            values={getReportsForDialogByUserRole(userRole, dailyOnly)}
            fullWidth
          />
        )}
        {canGenerateReportWithChosenData && (
          <Field
            name="generatedFor"
            component={FormSelect}
            label="Dane dla"
            values={ReportDataFor}
            fullWidth
            readonly={!accountingReportIds.includes(reportType)}
          />
        )}
        {!dailyOnly && sourceReportType === 1 && (
          <Field
            name="templateId"
            component={FormSelect}
            label="Szablon"
            values={customReportList}
            fullWidth
          />
        )}
      </div>
      {hasFullDayPeriod ? (
        <div>
          <Field name="startDate" component={FormDateField} label="Od dnia" preserveTimezone
            disableFuture normalize={(value) => hourlyRentalsDate(value)} />
          <Field name="endDate" component={FormDateField} label="Do dnia" preserveTimezone
            disableFuture normalize={(value) => hourlyRentalsDate(value, true)} />
          <p>{reportType === ReportTypeFields.clientFundsReport.value
            ? "Pełne, zakończone dni w czasie polskim."
            : "Pełne, zakończone dni w czasie polskim. Maksymalnie rok."}</p>
        </div>
      ) : periodicUnit ? (
        <div>
          <Field
            name="startDate"
            component={FormDateField}
            label={periodicUnit === "year" ? "Rok raportu" : "Miesiąc raportu"}
            views={periodicUnit === "year" ? ["year"] : ["year", "month"]}
            dateFormat={periodicUnit === "year" ? "YYYY" : "MM.YYYY"}
            preserveTimezone
            disableFuture
            normalize={(value) =>
              value && !value.isValid() ? value : periodicBikesRange(reportType, value).startDate
            }
            onChange={handlePeriodicDateChange}
          />
          <p>Zestawienie zapisanych raportów dziennych ze wskazaniem brakujących dni.</p>
        </div>
      ) : isDailyReport ? (
        <div>
          <Field
            name="startDate"
            component={FormDateField}
            label="Dzień raportu"
            disableFuture
            normalize={(value) =>
              value ? value.clone().startOf("day") : value
            }
            onChange={handleDailyReportDateChange}
          />
        </div>
      ) : (
        <React.Fragment>
          <div>
            <Field
              name="startDate"
              component={FormDateTimeField}
              label="Od"
              disableFuture
            />
          </div>
          <div>
            <Field name="endDate" component={FormDateTimeField} label="Do" />
          </div>
        </React.Fragment>
      )}
      {canShowAdditionalRange && (
        <div style={{ paddingTop: 15 }}>
          <div style={{ fontSize: 18 }}>Dodatkowy zakres</div>
          <div>
            <Field
              name="additionalStartDate"
              component={FormDateTimeField}
              label="Od"
              defaultValue={null}
            />
          </div>
          <div>
            <Field
              name="additionalEndDate"
              component={FormDateTimeField}
              label="Do"
              defaultValue={null}
            />
          </div>
        </div>
      )}
    </form>
  );
};

GenerateReportForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  customReportList: PropTypes.arrayOf(PropTypes.object).isRequired,
  sourceReportType: PropTypes.number,
  reportType: PropTypes.string,
  userRole: PropTypes.string,
  dailyOnly: PropTypes.bool,
  canGenerateReportWithChosenData: PropTypes.bool,
};

GenerateReportForm = reduxForm({
  form: "generateReport",
  validate: formValidation,
})(GenerateReportForm);

const selector = formValueSelector("generateReport");
GenerateReportForm = connect((state) => {
  const sourceReportType = selector(state, "sourceReportType");
  const reportType = selector(state, "type");
  return {
    sourceReportType: sourceReportType,
    reportType,
    userRole: state.auth.currentUserData.userRole,
    initialValues: {
      startDate: moment().local().add(-1, "month"),
      endDate: moment().local(),
      sourceReportType: 0,
      additionalStartDate: null,
      additionalEndDate: null,
      generatedFor: hasPermission(
        state.auth.permissions,
        permissions.reports.generateWithChosenData,
      )
        ? ReportDataFor.all.value
        : ReportDataFor.operator.value,
    },
  };
})(GenerateReportForm);

function mapStateToProps(state) {
  return {
    dailyOnly: hasPermission(state.auth.permissions, permissions.reports.daily) &&
      !hasPermission(state.auth.permissions, permissions.reports.generate),
    canGenerateReportWithChosenData: hasPermission(
      state.auth.permissions,
      permissions.reports.generateWithChosenData,
    ),
  };
}

export default connect(mapStateToProps)(GenerateReportForm);
